// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

export interface BrandingInitRequest {
  tenantId: string;
  subdomain?: string;
  displayName?: string;
  brandingTier?: 'NONE' | 'GREY_LABEL' | 'WHITE_LABEL';
  logos?: Record<string, string>;
  branding?: Record<string, unknown>;
}

export interface TenantBranding {
  id: string;
  tenantId: string;
  subdomain: string;
  displayName: string;
  brandingTier: 'NONE' | 'GREY_LABEL' | 'WHITE_LABEL';
  logos: Record<string, string>;
  branding: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface BrandingConfigDTO {
  tenantId: string;
  displayName: string;
  brandingTier: 'NONE' | 'GREY_LABEL' | 'WHITE_LABEL';
  logoUrl: string;
  showPoweredByBadge: boolean;
  logos: Record<string, string>;
  branding: Record<string, unknown>;
}

export interface BrandingPageResponse {
  content: TenantBranding[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

export interface MigrationReport {
  total: number;
  migrated: number;
  failed: number;
  errors: string[];
}

const API_BASE = '/api/branding';

export const brandingApi = {
  async initBranding(request: BrandingInitRequest): Promise<TenantBranding> {
    const response = await fetch(`${API_BASE}/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error('Failed to initialize branding');
    }
    return response.json();
  },

  async migrateBranding(records: BrandingInitRequest[]): Promise<MigrationReport> {
    const response = await fetch(`${API_BASE}/migrate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(records),
    });
    if (!response.ok) {
      throw new Error('Failed to migrate branding');
    }
    return response.json();
  },

  async updateBranding(tenantId: string, branding: Record<string, unknown>): Promise<TenantBranding> {
    const response = await fetch(`${API_BASE}/${tenantId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ branding }),
    });
    if (!response.ok) {
      throw new Error('Failed to update branding');
    }
    return response.json();
  },

  async updateLogos(tenantId: string, logos: Record<string, string>): Promise<TenantBranding> {
    const response = await fetch(`${API_BASE}/${tenantId}/logos`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ logos }),
    });
    if (!response.ok) {
      throw new Error('Failed to update logos');
    }
    return response.json();
  },

  async updateTier(tenantId: string, brandingTier: string): Promise<TenantBranding> {
    const response = await fetch(`${API_BASE}/${tenantId}/tier`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ brandingTier }),
    });
    if (!response.ok) {
      throw new Error('Failed to update branding tier');
    }
    return response.json();
  },

  async getConfig(tenantId: string): Promise<BrandingConfigDTO> {
    const response = await fetch(`${API_BASE}/config/${tenantId}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to fetch branding config');
    }
    return response.json();
  },

  async listAll(params?: {
    page?: number;
    size?: number;
    search?: string;
    tier?: string;
  }): Promise<BrandingPageResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', String(params.page));
    if (params?.size !== undefined) queryParams.append('size', String(params.size));
    if (params?.search) queryParams.append('search', params.search);
    if (params?.tier) queryParams.append('tier', params.tier);

    const response = await fetch(`${API_BASE}/admin/all?${queryParams}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to fetch branding list');
    }
    return response.json();
  },

  async getRawBranding(tenantId: string): Promise<TenantBranding> {
    const response = await fetch(`${API_BASE}/admin/${tenantId}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to fetch branding');
    }
    return response.json();
  },
};
